import { wrapHigherOrderComponent } from 'Lib/React';
import React, { ErrorInfo } from 'react';
import Message from '../Message';

interface IErrorBoundaryProps {}

interface IErrorBoundaryState {
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends React.Component<React.PropsWithChildren<IErrorBoundaryProps>, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = { error: undefined, errorInfo: undefined };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Catch errors in any components below and re-render with error message
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    // We can also log error messages to an error reporting service here if we want to
  }

  render() {
    if (this.state.errorInfo) {
      // Error path - render the error message
      return (
        <Message severity="error" message={this.state.error ? this.state.error.toString() : 'Something went wrong'}>
          <details style={{ whiteSpace: 'pre-wrap' }}>{this.state.errorInfo.componentStack}</details>
        </Message>
      );
    }

    // Normally, just render children
    return this.props.children;
  }
}

/**
 * Wraps a component in an error boundary
 *
 * @param Component The component to wrap.
 */
const withErrorBoundary = <P,>(Component: React.ComponentType<P>): React.FC<P & JSX.IntrinsicAttributes> =>
  wrapHigherOrderComponent(
    (props: P & JSX.IntrinsicAttributes) => (
      <ErrorBoundary>
        <Component {...props} />
      </ErrorBoundary>
    ),
    Component,
    'WithErrorBoundary'
  );

export { ErrorBoundary, withErrorBoundary };
export default ErrorBoundary;
